import buildSearchParams from 'Pods/SearchPage/Store/buildSearchParams'
import { ASSET_TYPE } from 'Pods/SearchPage/Store/constants'
import { pristineFilterState, searchPageStore } from 'Pods/SearchPage/Store/index'

import { FILTER_NAME } from './constants'
import { getPageAttributes } from './pageAttributes'
import safely from './safely'

const CONTENT_TYPE = {
  [ASSET_TYPE.ALL]: 'all',
  [ASSET_TYPE.THREE_D_ONLY]: 'model',
  [ASSET_TYPE.THREE_D_PRINT_ONLY]: '3d print',
}

const formatValue = (value) => (
  value && typeof value === 'object' ? Object.values(value).join('-') : String(value)
)

const toPairs = (params) => Object.entries(params).flatMap(([name, value]) => (
  (Array.isArray(value) ? value : [value]).map((item) => `${name}=${formatValue(item)}`)
))

// The default page already sends tokens (e.g. licenses=exclude_ai_generated for the default
// content source), so those are dropped: filters_selected lists only what the viewer changed.
let defaultPairs = null

const getDefaultPairs = () => {
  if (!defaultPairs) {
    defaultPairs = new Set(toPairs(buildSearchParams(pristineFilterState)))
  }
  return defaultPairs
}

const getKeywords = () => (
  typeof window === 'undefined' ? '' : new URLSearchParams(window.location.search).get('keywords') || ''
)

export const getContentType = (state = searchPageStore.getState()) => (
  CONTENT_TYPE[state.assetType] || CONTENT_TYPE[ASSET_TYPE.ALL]
)

// Same `name=value/name=value` string the legacy listing page builds in UserTracker's FilterTracker.
export const getFiltersSelected = (state = searchPageStore.getState()) => {
  const categoryPairs = [
    state.parentCategorySlug && `${FILTER_NAME.CATEGORY}=${state.parentCategorySlug}`,
    state.subcategorySlug && `subcategory=${state.subcategorySlug}`,
  ].filter(Boolean)
  const filterPairs = toPairs(buildSearchParams(state)).filter((pair) => !getDefaultPairs().has(pair))

  return [...categoryPairs, ...filterPairs].join('/').replace(/ /g, '_')
}

const pushEvent = ({ eventName, eventSource, eventData }) => {
  if (typeof window === 'undefined') {
    return
  }

  window.dataLayer = window.dataLayer || []
  window.dataLayer.push({
    event: 'GTMevent',
    event_name: eventName,
    ...(eventSource && { event_source: eventSource }),
    ...getPageAttributes(),
    event_data: eventData,
    _clear: true,
  })
}

// The legacy page reports the category slug as the search term of a keyword-less category page on
// filter_select and reset_click, while search_content keeps the keywords alone.
const getFilterSearchTerm = (state) => getKeywords() || state.parentCategorySlug || ''

/**
 * filter_select for one selected value. Multi-value selections call it once per value.
 */
export const trackFilterSelect = safely(({ name, value, eventSource }) => {
  if (value === undefined || value === null || value === '') {
    return
  }

  const state = searchPageStore.getState()

  pushEvent({
    eventName: 'filter_select',
    eventSource,
    eventData: {
      search_term: getFilterSearchTerm(state),
      content_type: getContentType(state),
      filter_name: name,
      filter_value: String(value),
    },
  })
})

export const trackSearchContent = safely(() => {
  const state = searchPageStore.getState()

  pushEvent({
    eventName: 'search_content',
    eventData: {
      search_term: getKeywords(),
      content_type: getContentType(state),
      filters_selected: getFiltersSelected(state),
    },
  })
})

export const trackResultsCount = safely(() => {
  const state = searchPageStore.getState()
  const { totalCount = 0, currentPage = 1 } = state.meta || {}

  pushEvent({
    eventName: 'results_count',
    eventData: {
      search_results_count: Number(totalCount) || 0,
      search_term: getKeywords(),
      content_type: getContentType(state),
      page: Number(currentPage) || 1,
    },
  })
})

// Called before the reset is applied, so the count and filters are the ones being cleared.
export const trackResetClick = safely(({ eventSource } = {}) => {
  const state = searchPageStore.getState()

  pushEvent({
    eventName: 'reset_click',
    eventSource,
    eventData: {
      search_term: getFilterSearchTerm(state),
      number_of_models_found: Number(state.meta?.totalCount) || 0,
      filters_selected: getFiltersSelected(state),
    },
  })
})
